// Client for the Worker's subscription API (worker/src/index.ts). Same origin, JSON in and out.

import type { SubscriptionStatus } from './rules';

export interface PlanAnswer {
  available: boolean;
  reason?: 'notOffered' | 'notConfigured';
  priceId?: string;
  currency?: string;
  /** Minor units (cents). */
  unitAmount?: number;
  interval?: 'day' | 'week' | 'month' | 'year';
  intervalCount?: number;
  trialDays?: number;
  taxBehavior?: string | null;
}

export interface EntitlementAnswer {
  token?: string;
  status: SubscriptionStatus | 'none';
  accessUntil?: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }

  /** No answer at all: offline, DNS, TLS, the Worker unreachable. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

async function call<T>(path: string, init?: RequestInit, timeoutMs = 15_000): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(path, { ...init, signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
  } catch {
    throw new ApiError(0, 'network');
  } finally {
    clearTimeout(timer);
  }
  const body = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new ApiError(response.status, body.error ?? `http_${response.status}`);
  return body;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return call<T>(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}

export const subscriptionApi = {
  plans: () => call<PlanAnswer>('/api/plans'),
  checkout: (email: string, locale: string) => post<{ url: string; trialDays: number }>('/api/checkout', { email, locale }),
  completeCheckout: (sessionId: string) =>
    call<EntitlementAnswer>(`/api/checkout/complete?session_id=${encodeURIComponent(sessionId)}`),
  refresh: (token: string) => post<EntitlementAnswer>('/api/entitlement/refresh', { token }),
  portal: (token: string, locale: string) => post<{ url: string }>('/api/portal', { token, locale }),
  restoreByEmail: (email: string, locale: string) => post<{ ok: boolean }>('/api/restore/email', { email, locale }),
  redeemRestoreCode: (code: string) => post<EntitlementAnswer>('/api/restore/redeem', { code }),
  createTransferCode: (token: string) => post<{ code: string; expiresAt: number }>('/api/transfer/create', { token }),
  redeemTransferCode: (code: string) => post<EntitlementAnswer>('/api/transfer/redeem', { code }),
};

export type SubscriptionApi = typeof subscriptionApi;
